/*
 * Time primitives (spec 6.1). Instants are whole seconds since the Unix epoch (UTC); local dates
 * are ISO strings `yyyy-MM-dd`. Time zone conversions use `Intl` with IANA identifiers, so they are
 * DST-safe and need no library. The rules match `ZonedTime` in the backend.
 */

/** Seconds since 1970-01-01T00:00:00Z (UTC), always whole seconds. */
export type EpochSeconds = number;

/** Local calendar date `yyyy-MM-dd`. */
export type LocalDate = string;

/** Seconds per minute, hour and day. */
export const secondsPerMinute = 60;
export const secondsPerHour = 3600;
export const secondsPerDay = 86_400;

const transitionProbeSeconds = 6 * secondsPerHour;
const localDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/u;
const localTimePattern = /^(\d{2}):(\d{2})(?::(\d{2}))?$/u;

/** Local date and time parts in a time zone. */
export interface LocalDateTimeParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

/**
 * Parses an ISO 8601 instant with offset and truncates it to whole seconds.
 *
 * @param iso - The instant, for example `2026-09-24T08:00:00+02:00`.
 * @returns Epoch seconds.
 * @throws RangeError if the text is not a valid instant.
 */
export function parseInstant(iso: string): EpochSeconds {
  const milliseconds = Date.parse(iso);

  if (Number.isNaN(milliseconds) || !/[zZ]|[+-]\d{2}:?\d{2}$/u.test(iso)) {
    throw new RangeError(`Invalid instant: '${iso}'.`);
  }

  const seconds = Math.floor(milliseconds / 1000);
  return seconds;
}

/**
 * Formats an instant as ISO 8601 in UTC (`…Z`), as the API expects.
 *
 * @param seconds - Epoch seconds.
 * @returns The ISO text without fractional seconds.
 */
export function formatInstant(seconds: EpochSeconds): string {
  const iso = new Date(seconds * 1000).toISOString().replace(".000Z", "Z");

  return iso;
}

/**
 * Converts milliseconds (for example from `Date.now()`) to whole epoch seconds.
 *
 * @param milliseconds - Epoch milliseconds.
 * @returns Epoch seconds, truncated.
 */
export function toEpochSeconds(milliseconds: number): EpochSeconds {
  const seconds = Math.floor(milliseconds / 1000);

  return seconds;
}

/**
 * Checks whether a text is a known IANA time zone identifier.
 *
 * @param timeZone - The identifier.
 * @returns `true` if `Intl` knows the zone.
 */
export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone.trim() === "" || (!timeZone.includes("/") && timeZone !== "UTC")) {
    return false;
  }

  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns the local date and time of an instant in a time zone.
 *
 * @param seconds - Epoch seconds.
 * @param timeZone - IANA time zone.
 * @returns The local parts.
 */
export function toLocalParts(seconds: EpochSeconds, timeZone: string): LocalDateTimeParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const values = new Map<string, number>();

  for (const part of formatter.formatToParts(new Date(seconds * 1000))) {
    if (part.type !== "literal") {
      values.set(part.type, Number(part.value));
    }
  }

  const parts: LocalDateTimeParts = {
    year: values.get("year") ?? 0,
    month: values.get("month") ?? 0,
    day: values.get("day") ?? 0,
    hour: values.get("hour") ?? 0,
    minute: values.get("minute") ?? 0,
    second: values.get("second") ?? 0,
  };

  return parts;
}

/**
 * Returns the offset of a time zone from UTC at an instant.
 *
 * @param seconds - Epoch seconds.
 * @param timeZone - IANA time zone.
 * @returns Offset in seconds (positive east of Greenwich).
 */
export function offsetSeconds(seconds: EpochSeconds, timeZone: string): number {
  const parts = toLocalParts(seconds, timeZone);
  const localAsUtc =
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) / 1000;
  const offset = localAsUtc - seconds;

  return offset;
}

/**
 * Returns the local calendar date of an instant (spec 6.1.3).
 *
 * @param seconds - Epoch seconds.
 * @param timeZone - IANA time zone.
 * @returns The local date.
 */
export function toLocalDate(seconds: EpochSeconds, timeZone: string): LocalDate {
  const parts = toLocalParts(seconds, timeZone);
  const date = formatLocalDate(parts.year, parts.month, parts.day);

  return date;
}

/**
 * Formats a date from its parts.
 *
 * @param year - Year.
 * @param month - Month 1–12.
 * @param day - Day 1–31.
 * @returns The local date.
 */
export function formatLocalDate(year: number, month: number, day: number): LocalDate {
  const text = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  return text;
}

/**
 * Parses a local date and validates it against the calendar.
 *
 * @param date - The date `yyyy-MM-dd`.
 * @returns Year, month and day.
 * @throws RangeError for invalid dates such as `2026-02-30`.
 */
export function parseLocalDate(date: LocalDate): { year: number; month: number; day: number } {
  const match = localDatePattern.exec(date);
  const year = Number(match?.[1]);
  const month = Number(match?.[2]);
  const day = Number(match?.[3]);
  const check = new Date(Date.UTC(year, month - 1, day));

  if (
    match === null ||
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    throw new RangeError(`Invalid local date: '${date}'.`);
  }

  const parts = { year, month, day };
  return parts;
}

/**
 * Adds calendar days to a local date.
 *
 * @param date - The date.
 * @param days - Number of days (may be negative).
 * @returns The shifted date.
 */
export function addDays(date: LocalDate, days: number): LocalDate {
  const { year, month, day } = parseLocalDate(date);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  const result = formatLocalDate(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
  );

  return result;
}

/**
 * Returns the ISO weekday of a local date.
 *
 * @param date - The date.
 * @returns 1 (Monday) to 7 (Sunday).
 */
export function isoWeekday(date: LocalDate): number {
  const { year, month, day } = parseLocalDate(date);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const iso = weekday === 0 ? 7 : weekday;

  return iso;
}

/**
 * Converts a local date and time in a time zone to an instant.
 *
 * Rules as in the backend (ADR-0006): a local time inside a DST gap is shifted forward by the
 * length of the gap; an ambiguous local time (autumn) resolves to its first occurrence, or with
 * `laterIfAmbiguous` to its second one (the end of an interval of work, F-50).
 *
 * @param date - Local date.
 * @param time - Local time `HH:mm` or `HH:mm:ss`.
 * @param timeZone - IANA time zone.
 * @param laterIfAmbiguous - Whether an ambiguous local time means its later occurrence.
 * @returns Epoch seconds.
 * @throws RangeError for malformed input.
 */
export function fromLocalDateTime(
  date: LocalDate,
  time: string,
  timeZone: string,
  laterIfAmbiguous = false,
): EpochSeconds {
  const { year, month, day } = parseLocalDate(date);
  const match = localTimePattern.exec(time);
  const hour = Number(match?.[1]);
  const minute = Number(match?.[2]);
  const second = Number(match?.[3] ?? "0");

  if (match === null || hour > 23 || minute > 59 || second > 59) {
    throw new RangeError(`Invalid local time: '${time}'.`);
  }

  const localAsUtc = Date.UTC(year, month - 1, day, hour, minute, second) / 1000;
  const offsetBefore = offsetSeconds(localAsUtc - transitionProbeSeconds, timeZone);
  const offsetAfter = offsetSeconds(localAsUtc + transitionProbeSeconds, timeZone);
  const candidates = [...new Set([offsetBefore, offsetAfter])].filter(
    (offset) => offsetSeconds(localAsUtc - offset, timeZone) === offset,
  );
  let offset: number;

  if (candidates.length === 0) {
    // Gap: apply the smaller (earlier) offset, which moves the time forward by the gap length.
    offset = Math.min(offsetBefore, offsetAfter);
  } else {
    // Ambiguous: the larger offset belongs to the first occurrence, the earlier instant.
    offset = laterIfAmbiguous ? Math.min(...candidates) : Math.max(...candidates);
  }

  const instant = localAsUtc - offset;
  return instant;
}

/**
 * Returns the first instant of the day after a local date in a time zone: the next local midnight,
 * in summer and winter time alike. Where a clock change skips midnight, the day begins with the
 * first local time after the gap (rules of {@link fromLocalDateTime}).
 *
 * @param date - Local date.
 * @param timeZone - IANA time zone.
 * @returns Epoch seconds at which the next day begins.
 */
export function startOfNextDay(date: LocalDate, timeZone: string): EpochSeconds {
  const instant = fromLocalDateTime(addDays(date, 1), "00:00", timeZone);

  return instant;
}
